/**
 * Helpers purs pour le prefill silencieux du champ Prix dans `<VariantModal>`.
 *
 * Extraits ici pour permettre des tests unitaires propres sans mocker
 * tous les hooks RHF/TanStack du modal. Les fonctions sont pures :
 * elles ne lisent ni l'état RHF ni les queries — le caller leur passe les
 * données déjà résolues.
 *
 * Voir [`<VariantModal>`](./VariantModal.tsx) pour le contrat sticky du
 * prefill (one-shot par session du modal).
 *
 * Refonte tarifs 2026-05-15 — cascade simplifiée à 1 priorité unique :
 *   1. isDigital && atelier.digital_default_price_cents > 0
 *   2. sinon : null (pas de prefill)
 *
 * Sources retirées avec la refonte :
 *  - `'atelier_print'` : la matrice `print_prices` (triple format/framing/
 *    support → prix) a été supprimée (migration 012). Auto-calc POD via
 *    `pricing_service.compute_pod_retail_price_cents` rend la matrice
 *    obsolète.
 *  - `'pod'` : le résolveur 9-étapes a été retiré refonte POD v6.1
 *    (`podPreview` toujours `null` depuis). Et la refonte tarifs cache
 *    désormais le champ Prix pour POD ('open') — pas de surface où
 *    prefill.
 */

import type { AtelierSettingsPayload } from '@/types/atelier-settings'
import type { FormatResponse, FramingResponse, SupportResponse } from '@/types/catalog'

/**
 * Source qui a alimenté le prefill silencieux du champ Prix. Affichée en
 * petit label sous l'input. Disparaît dès que l'utilisatrice édite le champ.
 *
 * Refonte tarifs 2026-05-15 : `'atelier_digital'` est la seule source
 * restante (cf. cascade simplifiée).
 */
export type PrefillSource = { type: 'atelier_digital'; valueCents: number }

export function prefillSourceLabel(s: PrefillSource): string {
  switch (s.type) {
    case 'atelier_digital':
      return 'Tarif atelier digital'
  }
}

/**
 * Inputs de la cascade. Les champs `formatIds`/`framingId`/`supportId`/
 * `formats`/`framings`/`supports`/`podPreview`/`podPricing` sont conservés
 * dans la signature pour compatibilité avec les callers actuels (et tests),
 * mais ne sont plus consommés depuis la refonte tarifs 2026-05-15.
 */
export interface PrefillInputs {
  mode: 'create' | 'edit'
  isDigital: boolean
  formatIds: readonly (string | number)[]
  framingId: string
  supportId: string
  atelierConfig: AtelierSettingsPayload | null | undefined
  podPreview: unknown
  podPricing: unknown
  formats: readonly FormatResponse[]
  framings: readonly FramingResponse[]
  supports: readonly SupportResponse[]
}

export function computePrefillCandidate(
  inputs: PrefillInputs,
): { source: PrefillSource; cents: number } | null {
  const { isDigital, atelierConfig } = inputs
  // Champs ignorés depuis refonte tarifs 2026-05-15 — gardés pour signature stable.
  void inputs.mode
  void inputs.formatIds
  void inputs.framingId
  void inputs.supportId
  void inputs.podPreview
  void inputs.podPricing
  void inputs.formats
  void inputs.framings
  void inputs.supports

  // Cascade simplifiée — défaut atelier digital (valeur scalaire unique).
  if (
    isDigital &&
    atelierConfig?.digital_default_price_cents != null &&
    atelierConfig.digital_default_price_cents > 0
  ) {
    const cents = atelierConfig.digital_default_price_cents
    return { source: { type: 'atelier_digital', valueCents: cents }, cents }
  }

  return null
}
